<template>
  <div class="transferOut">
    <div v-if="loading">
      <v-progress-circular
        :size="20"
        color="primary"
        class="mr-2"
        indeterminate
      ></v-progress-circular>
      Loading...
    </div>

    <div v-else>
      <v-form
        ref="form"
        class="mb-4"
        v-model="isFormValid"
        lazy-validation
        :style="{ maxWidth: formMaxWidth + 'px' }"
      >
        <TabForm
          :formElements="formElements"
          :readOnly="!isEditMode"
          calculateMaxFormWidth
          @max_column="calculateMaxFormWidth"
        />
      </v-form>

      <LeaversBtns
        :isDataExist="isDataExist"
        :isEditMode="isEditMode"
        :showFormButtons="showFormButtons"
        :showViewResults="calculatedValues.length > 0"
        :showQuoteButton="calculatedValues.length > 0"
        :isFormValid="isFormValid"
        @buttonClick="onButtonClick"
      />

      <v-dialog v-model="showCalculation" max-width="500" persistent>
        <Calculation
          formName="TransferOut"
          :currentPlanId="currentPlanId"
          :selectedMemberId="selectedMemberId"
          @calculateClick="onCalculateClick"
        />
      </v-dialog>

      <v-dialog v-model="showCalculatedMessages" max-width="500" persistent>
        <CalculatedMessages
          :messages="calculatedMessages"
          @closeMessages="showCalculatedMessages = false"
        />
      </v-dialog>

      <v-dialog v-model="showCalculatedValues" max-width="500" persistent>
        <CalculatedValues :values="calculatedValues" @closeResults="showCalculatedValues = false" />
      </v-dialog>

      <v-dialog v-model="showQuote" max-width="700" persistent>
        <Letters
          @closeQuote="showQuote = false"
          :memberId="selectedMemberId"
          :calculationResults="calculationResults"
        />
      </v-dialog>
    </div>
    <Dialog ref="confirmationDialog"></Dialog>
  </div>
</template>

<script>
import TabForm from '@/components/TabForm.vue';
import LeaversBtns from './LeaversBtns.vue';
import Utils from '@/utils.js';
import Calculation from './Calculation.vue';
import CalculatedMessages from './CalculatedMessages.vue';
import CalculatedValues from './CalculatedValues.vue';
import Letters from './Letters.vue';
import Dialog from '@/components/layout/Dialog.vue';

export default {
  name: 'TransferOut',
  components: {
    TabForm,
    LeaversBtns,
    Calculation,
    CalculatedMessages,
    CalculatedValues,
    Letters,
    Dialog,
  },
  props: {},

  data() {
    return {
      loading: false,
      formElements: [],
      isFormValid: false,
      exitData: [],
      isEditMode: false,
      selectedTransferOut: {},
      formMaxWidth: 500,
      showCalculation: false,
      calculatedValues: [],
      calculatedMessages: [],
      showCalculatedMessages: false,
      showCalculatedValues: false,
      showQuote: false,
      calculationResults: [],
    };
  },

  computed: {
    currentPlanId() {
      return this.$store.getters.currentPlanId;
    },
    selectedMemberId() {
      return this.$store.getters.selectedMemberId;
    },
    showFormButtons() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
    isNewForm() {
      if (this.selectedMemberId && !this.selectedTransferOut.ExitId) {
        return true;
      } else if (this.selectedMemberId && this.selectedTransferOut.ExitId) {
        return false;
      } else {
        return null;
      }
    },
    exitId() {
      return this.selectedTransferOut.ExitId;
    },
    isDataExist() {
      return Object.keys(this.selectedTransferOut).length > 0;
    },
  },

  watch: {
    selectedMemberId() {
      this.getTransferOutData();
    },
  },

  mounted() {
    if (!this.selectedMemberId) {
      this.$store.commit('setNotificationMessage', {
        type: 'info',
        text: 'Please, select member',
      });
      this.$router.push({ path: '/' });
    } else {
      this.getForm();
      this.$root.currentPage = 'Leavers';
    }
  },

  destroyed() {
    this.$root.currentPage = null;
  },

  methods: {
    async onButtonClick(val) {
      if (val === 'cancel') {
        if (this.isNewForm) {
          this.formElements.forEach((el) => (el.Value = null));
        } else {
          Utils.setDataToFormElements(this.selectedTransferOut, this.formElements);
        }

        this.isEditMode = false;
        this.calculatedMessages = [];
        this.calculatedValues = [];
      } else if (val === 'save') {
        this.isFormValid = this.$refs.form.validate();
        if (this.isFormValid) {
          this.isEditMode = false;
          this.saveData();
        }
      } else if (val === 'delete') {
        try {
          await this.$refs.confirmationDialog.open(
            'Confirmation',
            'Are you sure you wish to delete this record?'
          );
          this.deleteData();
        } catch (err) {
          console.log();
        }
      } else if (val === 'edit') {
        this.isEditMode = true;
      } else if (val === 'calculate') {
        this.showCalculation = true;
        this.isEditMode = true;
      } else if (val === 'viewResults') {
        this.showCalculatedValues = true;
      } else if (val === 'quote') {
        this.showQuote = true;
      }
    },

    onCalculateClick(val, data) {
      if (val === 'cancel') {
        this.showCalculation = false;
      } else if (val === 'calculate') {
        this.showCalculation = false;
        if (data) {
          this.calculationResults = data;
          this.calculatedValues = data.Values || [];
          this.calculatedMessages = data.Messages || [];

          if (this.calculatedMessages.length) {
            this.showCalculatedMessages = true;
          }

          if (data.Values && data.Values.length) {
            data.Values.forEach((value) => {
              const updatingEl = this.formElements.find((el) => el.FieldName === value.FieldName);
              if (updatingEl) {
                updatingEl.Value = value.Value;
              }
            });
          }
        }
      }
    },

    calculateMaxFormWidth(maxColumnCount) {
      this.formMaxWidth = maxColumnCount * 400;
    },

    async getForm() {
      this.loading = true;
      try {
        const response = await this.$api.get(
          `/datadictionary/GetFormElements?formName=TransferOut&planId=${this.currentPlanId}`
        );

        if (response.data.length) {
          response.data.sort((a, b) => a.FieldOrder - b.FieldOrder);
          this.formElements = response.data.filter((el) => el.Visible);
          if (this.formElements.length) this.getTransferOutData();
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
    async getTransferOutData() {
      this.loading = true;
      try {
        const response = await this.$api.get(
          '/TransferOut/GetTransferOutByMember?membershipId=' + this.selectedMemberId
        );

        if (response.data) {
          Utils.setDataToFormElements(response.data, this.formElements);
          this.selectedTransferOut = response.data;
        } else {
          this.formElements.forEach((el) => (el.Value = ''));
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async saveData() {
      this.loading = true;
      try {
        this.formElements.forEach((el) => {
          this.selectedTransferOut[el.FieldName] = el.Value;
        });
        this.selectedTransferOut.MembershipId = this.selectedMemberId;
        if (this.isNewForm) this.selectedTransferOut.ExitId = 0;

        await this.$api.post('/TransferOut/UpdateTransferOut', this.selectedTransferOut);

        this.$store.commit('setNotificationMessage', { type: 'success', text: 'Data was saved' });
      } catch (error) {
        console.log(error.message);
      } finally {
        this.getTransferOutData();
        this.loading = false;
      }
    },

    async deleteData() {
      this.loading = true;
      try {
        await this.$api.delete(
          '/TransferOut/DeleteTransferOut?transferId=' + this.selectedTransferOut.TransferId
        );

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Data was deleted',
        });
      } catch (error) {
        console.log(error.message);
      } finally {
        this.getTransferOutData();
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.transferOut {
  margin-bottom: 10px;
  padding-left: 30px;
  padding-right: 30px;
}

.footerBtns {
  background-color: white;
  padding: 10px 20px;
}
</style>
